import { useState } from 'react';
import { ArrowLeft, Search, BookOpen, Volume2, Square } from 'lucide-react';
import { commonPhrases } from '../data/phrases';

interface LibraryProps {
  onBack: () => void;
}

export function Library({ onBack }: LibraryProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const categories = Array.from(new Set(commonPhrases.map(p => p.category)));

  const filteredPhrases = commonPhrases.filter(phrase => {
    const matchesSearch = 
      phrase.transliteration.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phrase.meaning.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phrase.usage.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory ? phrase.category === selectedCategory : true;

    return matchesSearch && matchesCategory;
  });

  const handlePlayAudio = (id: string, text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      if (playingId === id) {
        setPlayingId(null);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'ar-SA'; // Arabic
      utterance.rate = 0.8; // Slightly slower for clarity
      
      utterance.onstart = () => setPlayingId(id);
      utterance.onend = () => setPlayingId(null);
      utterance.onerror = () => setPlayingId(null);
      
      window.speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col pb-8">
      {/* Header */}
      <div className="bg-white px-6 py-6 pt-safe-top border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={onBack}
            className="p-2 -ml-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="text-center">
            <h2 className="text-xl font-bold flex items-center justify-center gap-2">
              The Library <BookOpen className="w-5 h-5 text-emerald-600" />
            </h2>
            <p className="text-sm text-slate-500">Essential phrases for your journey</p>
          </div>
          <div className="w-10"></div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search phrases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-100 pl-10 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              selectedCategory === null 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 p-4 space-y-4">
        {filteredPhrases.map((phrase) => (
          <div key={phrase.id} className="bg-white/70 dark:bg-slate-800/60 backdrop-blur-lg p-5 rounded-2xl border border-white/50 dark:border-slate-700/50 shadow-sm hover:shadow-md transition-all">
            <div className="flex justify-between items-start mb-3">
              <div>
                <span className="inline-block px-2 py-0.5 rounded bg-emerald-50/80 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-wider mb-2 backdrop-blur-sm">
                  {phrase.category}
                </span>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{phrase.transliteration}</h3>
              </div>
              <div className="flex flex-col items-end gap-2">
                <p className="font-serif text-xl text-emerald-800 dark:text-emerald-400" dir="rtl">{phrase.arabic}</p>
                <button 
                  onClick={() => handlePlayAudio(phrase.id, phrase.arabic)}
                  className={`p-2 rounded-full transition-all backdrop-blur-sm ${
                    playingId === phrase.id
                      ? 'bg-emerald-500 text-white animate-pulse'
                      : 'bg-emerald-50/80 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-800/50'
                  }`}
                  title={playingId === phrase.id ? "Stop" : "Listen"}
                >
                  {playingId === phrase.id ? (
                    <Square className="w-4 h-4 fill-current" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 border-l-2 border-emerald-200 dark:border-emerald-700 pl-3">
                "{phrase.meaning}"
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed bg-slate-50/50 dark:bg-slate-900/30 p-3 rounded-lg backdrop-blur-sm">
                <span className="font-bold text-slate-600 dark:text-slate-300">When to use:</span> {phrase.usage}
              </p>
            </div>
          </div>
        ))}

        {filteredPhrases.length === 0 && (
          <div className="text-center py-20 text-slate-400">
            <p>No phrases found.</p>
          </div>
        )}
      </div>
    </div>
  );
}
